/**
 * QuestionnairePanel.tsx — the deal drawer's Questionnaire tab (2026-07-21,
 * PR 4/5): the clinician's in-drawer, SQL-tagged form home.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * Four states (READ-ONLY = the answer summary since the 2026-08-27 drawer
 * restructure — the greyed disabled-form replica is GONE from this tab: "hard
 * to see what they submitted". The EDITABLE SinglePageForm keeps its real
 * job; see QuestionnaireSummary.tsx for the summary's contract):
 *   1. NO assessment yet (a fresh Create-New SQL deal, or an MQL lead) —
 *      CLINICIAN only: the Start panel captures height/weight (the BMI
 *      inputs a lead needs), mints the Lead (skipped when the deal already
 *      has one) + the STAFF assessment (origin='clinician' — the authed
 *      staff endpoint, same reasoning as the retired ClinicianFormPage),
 *      and the clinic-service hooks auto-link both to this deal through the
 *      shared contact email. Admins without an assessment never see the tab
 *      at all — DealDrawer hides it; the role guard here is defence-in-depth.
 *   2. Assessment in progress — CLINICIAN: the EDITABLE SinglePageForm,
 *      prefilled from the raw answers read ("previously-filled forms");
 *      ADMIN: the read-only answer summary of what is recorded so far.
 *   3. Submitted — the Wave-4 AUTHORISE→RECOMMEND flow for a clinician
 *      (ClinicianResultPanel): the ANSWER SUMMARY (with the per-answer note
 *      affordance threaded through the summary's `renderItemExtra`) under
 *      ONE bottom-right "Authorise questionnaire" CTA, then the
 *      recommendation step; ADMIN gets the summary + a read-only note.
 *   4. Sticky not_eligible (CLIN-03) — a hard stop stamps eligibility on the
 *      assessment while it may still be `draft`. A LIVE editing session keeps
 *      its frozen SinglePageForm mounted (the hard-stop dialog and the
 *      just-given answers live inside it — swapping to the summary mid-stop
 *      would yank the dialog from under the clinician); every FRESH mount of
 *      the tab renders the banner + the read-only summary instead, and the
 *      modal dialog never re-fires on remount.
 */
import { useEffect, useRef, useState } from 'react';
import { createStaffAssessment } from '../api/clinic';
import { getAssessmentAnswers } from '../api/deals';
import { createLead } from '../api/journey';
import type { AssessmentSubmitResult, DealCard, DealDetail, Eligibility, UserRole } from '../api/types';
import { ClinicianResultPanel } from '../clinicians/ClinicianResultPanel';
import { Button, Field, Help, TextInput } from '../ui/kit';
import { DrawerSkeleton } from '../ui/skeletons';
import { NotEligibleBanner } from './stageClarity';
import { QuestionnaireSummary } from './QuestionnaireSummary';
import { SubmittedPhotos } from './SubmittedPhotos';
import { SinglePageForm } from './SinglePageForm';

export function QuestionnairePanel({
  deal,
  assessment,
  role,
  onChanged,
  onStopped,
}: {
  deal: DealCard;
  assessment: DealDetail['assessment'];
  role: UserRole;
  /** The deal moved / linked something new — reload the drawer + board. */
  onChanged: () => void;
  /**
   * Hard stop / not_eligible landed. Defaults to onChanged. Prefer a soft
   * board-only refresh here: CLIN-04 under-13 purge deletes the deal, and a
   * hard detail reload would replace the stop dialog with a 404.
   */
  onStopped?: () => void;
}) {
  const [started, setStarted] = useState<{ assessmentId: string; heightCm: number; weightKg: number } | null>(null);
  const [prefill, setPrefill] = useState<Record<string, unknown> | null>(null);
  const [prefillDetails, setPrefillDetails] = useState<Record<string, string>>({});
  const [submitResult, setSubmitResult] = useState<AssessmentSubmitResult | null>(null);

  const assessmentId = started?.assessmentId ?? assessment?.assessmentId ?? null;
  const resuming = !started && assessment != null;

  const submitted = assessment?.state === 'submitted' || assessment?.state === 'rejected';
  const eligibility = assessment?.eligibility ?? null;
  // CLIN-03 — hard stop stamps not_eligible (often while still draft). Treat
  // that the same as a submitted not_eligible: frozen, never authorise flow.
  const notEligible = eligibility === 'not_eligible';
  // The EDITABLE staff form's home turf: a CLINICIAN on an open draft.
  const editableForm = role === 'CLINICIAN' && !submitted && !notEligible;
  // TRUE once THIS mount has rendered the editable form. A hard stop flips
  // `notEligible` mid-session, and the stop dialog + the frozen just-given
  // answers live INSIDE SinglePageForm — so a live session must keep the form
  // mounted (frozen) rather than swap to the summary under the clinician's
  // cursor. A fresh mount of an already-stopped assessment starts false and
  // renders the summary. Render-phase ref write is deliberate and idempotent.
  const editedThisMount = useRef(false);
  if (editableForm) editedThisMount.current = true;

  // "Clinicians see previously-filled forms" — the raw answer values (and any
  // yes_no_text details) prefill the page when resuming an existing
  // assessment. ONLY the editable-form lanes need it (the summary reads the
  // review projection instead); a fresh start has none, and a FAILED read
  // falls back to an empty prefill so the form still renders rather than
  // spinning forever.
  useEffect(() => {
    if (!resuming || !assessment || !editedThisMount.current) return;
    let cancelled = false;
    getAssessmentAnswers(assessment.assessmentId)
      .then((read) => {
        if (cancelled) return;
        setPrefill(read.answers);
        setPrefillDetails(read.details ?? {});
      })
      .catch(() => {
        if (!cancelled) setPrefill({});
      });
    return () => {
      cancelled = true;
    };
  }, [resuming, assessment]);

  if (submitResult) {
    return <ClinicianResultPanel result={submitResult} clinicSlug={deal.clinicSlug} />;
  }

  if (!assessmentId) {
    // Defence-in-depth: DealDrawer never renders this tab for a non-CLINICIAN
    // without an assessment, but the Start panel must stay clinician-only even
    // if a future caller mounts the panel directly.
    if (role !== 'CLINICIAN') {
      return <Help>No questionnaire yet — the questionnaire starts from the clinician side.</Help>;
    }
    return (
      <StartQuestionnaire
        deal={deal}
        onStarted={(next) => {
          setStarted(next);
          onChanged();
        }}
      />
    );
  }

  // The LIVE editing session — the editable SinglePageForm's real job, kept
  // mounted (frozen, readOnly) across a mid-session hard stop so the stop
  // dialog and the just-given answers stay on screen (see editedThisMount).
  if (editableForm || editedThisMount.current) {
    if (resuming && prefill === null) return <DrawerSkeleton blocks={3} label="Loading the previous answers…" />;
    return (
      <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
        <SinglePageForm
          assessmentId={assessmentId}
          clinicSlug={deal.clinicSlug}
          capturePrefill={started ? { heightCm: started.heightCm, weightKg: started.weightKg } : undefined}
          prefill={prefill ?? {}}
          prefillDetails={prefillDetails}
          readOnly={!editableForm}
          alreadyNotEligible={notEligible}
          onSubmitted={(result) => {
            setSubmitResult(result);
            onChanged();
          }}
          onStopped={() => (onStopped ?? onChanged)()}
        />
      </div>
    );
  }

  // The Wave-4 authorise→recommend flow OWNS the submitted-clinician view —
  // now over the ANSWER SUMMARY (2026-08-27 restructure): the per-answer note
  // control threads through the summary's items so the ONE bottom-right CTA
  // sits under readable Q/A sections, never a disabled form replica.
  if (submitted && role === 'CLINICIAN' && !notEligible) {
    return (
      <ClinicianResultPanel
        result={{
          assessmentId,
          state: 'submitted',
          eligibility: (eligibility ?? 'clinician_review') as Eligibility,
          flags: [],
        }}
        clinicSlug={deal.clinicSlug}
        renderQuestionnaire={(noteControl) => (
          <QuestionnaireSummary
            assessmentId={assessmentId}
            consentAgreedAt={assessment?.consentAgreedAt ?? null}
            renderItemExtra={(item) => noteControl({ key: item.questionKey, prompt: item.prompt })}
          />
        )}
      />
    );
  }

  // Every remaining read-only lane (ADMIN draft/submitted, a fresh mount of a
  // sticky not_eligible stop — any role): the answer summary, with the state
  // named above it. The greyed replica is GONE from this tab (2026-08-27).
  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-3)' }}>
      {notEligible ? (
        /* CLIN-03 remount — THE SHARED BANNER, not a fourth hand-written copy.
         *
         * ⚠️ THIS TAB USED TO CONTRADICT THE OTHER TWO. Activity and History
         * were moved onto `NotEligibleBanner` (which reads the real stage) and
         * this one kept a hard-coded "the deal has moved to Lost" — so a single
         * click between Questionnaire and Activity flipped the same deal
         * between closed and open in front of the reader. The tab loop in
         * notEligibleStage.spec.tsx names all three tabs and now visits all
         * three; it did not, which is how this survived the fix that named it.
         *
         * role defaults to "status": standing state on a remount, not a new
         * event to announce (the LIVE form owns the assertive one). */
        <NotEligibleBanner
          stage={deal.stage}
          context="Advise the caller; the answers below are read-only, for your records."
        />
      ) : submitted ? (
        <Help>Submitted — read-only. Recommendations and clinical actions are clinician-side.</Help>
      ) : (
        <Help>In progress — the answers recorded so far, read-only.</Help>
      )}
      {/* ⚠️ NO PLAN-LINK CONTROLS HERE, AND THAT IS THE POINT (CTO 2026-09-05).
          They sat at the top of this tab for one wave and diluted it: this tab
          answers ONE question — what did the patient actually submit — and an
          action bar above the answers made a reading surface look like a
          working surface. Both controls now live once, at the FOOT of the
          Overview tab, which is where the rest of the deal's actions already
          are. Re-adding them here is a regression, and adminSubmitted.spec.tsx
          fails if anyone does. */}
      <QuestionnaireSummary assessmentId={assessmentId} consentAgreedAt={assessment?.consentAgreedAt ?? null} />
      {/* WHAT THE PATIENT SENT, IN ONE PLACE (2026-08-29, CTO on stage: "they
          can't see the photos", and "questionnaire and review show the same
          thing on admin").

          Both complaints are the same gap. This read-only lane is where an
          ADMIN lands on the Questionnaire tab, and until now it rendered the
          answer summary and nothing else — the same summary the Review tab
          already shows, which is why the two tabs read as duplicates. The
          photos, meanwhile, existed on ONE surface: the clinician gallery
          inside Review. So the tab that belongs to admins showed a copy of
          somebody else's tab and left out half of what the patient submitted.

          Adding the photos here makes the tab mean something on its own:
          Questionnaire is "everything the patient sent us", Review stays the
          clinical decision surface. It also stays the right side of the safety
          line — see SubmittedPhotos for why the clinician's attestation
          component is deliberately NOT reused here. */}
      <SubmittedPhotos assessmentId={assessmentId} />
    </div>
  );
}

/** Height/weight capture → Lead (unless the deal has one) + staff assessment. */
function StartQuestionnaire({
  deal,
  onStarted,
}: {
  deal: DealCard;
  onStarted: (next: { assessmentId: string; heightCm: number; weightKg: number }) => void;
}) {
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const h = Number(heightCm);
  const w = Number(weightKg);
  const valid = Number.isFinite(h) && Number.isFinite(w) && h > 0 && w > 0;

  async function start() {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      let leadId = deal.leadId;
      if (!leadId) {
        const lead = await createLead({
          clinicSlug: deal.clinicSlug,
          email: deal.contact.email,
          heightCm: h,
          weightKg: w,
          displayUnits: 'metric',
          ...(deal.contact.firstName ? { firstName: deal.contact.firstName } : {}),
          ...(deal.contact.lastName ? { lastName: deal.contact.lastName } : {}),
          ...(deal.contact.phone ? { phone: deal.contact.phone } : {}),
        });
        leadId = lead.leadId;
      }
      const created = await createStaffAssessment({ clinicSlug: deal.clinicSlug, leadId });
      onStarted({ assessmentId: created.assessmentId, heightCm: h, weightKg: w });
    } catch {
      setError('Sorry — we could not start the questionnaire. Please try again.');
      setBusy(false);
    }
  }

  return (
    <div>
      <Help>
        Fill the eligibility form on the caller&rsquo;s behalf — enter THEIR details exactly as given.
        It is recorded as a clinician-initiated form for {deal.contact.email}.
      </Help>
      <div style={{ display: 'flex', gap: 'var(--gps-s-3)', maxWidth: '420px' }}>
        <Field label="Height (cm)" htmlFor="deal-q-height">
          <TextInput id="deal-q-height" inputMode="decimal" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} />
        </Field>
        <Field label="Weight (kg)" htmlFor="deal-q-weight">
          <TextInput id="deal-q-weight" inputMode="decimal" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} />
        </Field>
      </div>
      {error ? (
        <p role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '13.5px' }}>
          {error}
        </p>
      ) : null}
      <Button
        type="button"
        variant="cta"
        disabled={!valid || busy}
        onClick={() => void start()}
        style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
      >
        {busy ? 'Starting…' : 'Start the questionnaire'}
      </Button>
    </div>
  );
}
